import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import formatApiMessage from '../../utils/format-api-message.ts';
import { RoutesPermissionEnum as RP } from '../../utils/enums.ts';

const requiredColumns = [
  'UsageType',
  'Answer',
  'Domain',
  'Skill/Knowledge',
  'Difficulty',
  'Question_Content',
  'Question_Prompt',
];

const optionalColumns = [
  { label: 'QID' },
  { label: 'QSourceID' },
  { label: 'QSourceRelation', note: 'requires QSourceID' },
  { label: 'Qsub' },
  { label: 'Choice_A', note: 'required for multiple choice' },
  { label: 'Choice_B', note: 'required for multiple choice' },
  { label: 'Choice_C', note: 'required for multiple choice' },
  { label: 'Choice_D', note: 'required for multiple choice' },
  { label: 'Qtype1' },
  { label: 'Qtype2', note: 'requires Qtype1' },
  { label: 'Tag' },
  { label: 'QExplain' },
  { label: 'Graph' },
];

type ImportResponse = {
  id: number;
  insertedCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  summary?: {
    phase?: 'Staged' | 'Applied';
    errors?: Array<{
      rowNumber: number;
      reference: string;
      messages: unknown[];
    }>;
  };
};

const getWorkbookErrorMessage = (
  errors?: Array<{
    rowNumber: number;
    reference: string;
    messages: unknown[];
  }>,
) => {
  const workbookError = errors?.find((item) => item.rowNumber === 0);

  if (!workbookError?.messages?.length) {
    return '';
  }

  return formatApiMessage(workbookError.messages);
};

const DataUpload = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  const uploadFile = async (file: File) => {
    const token = await getAccessTokenSilently();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${import.meta.env.VITE_API_URL}/question/import`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = (await response.json().catch(() => null)) as ImportResponse & {
      message?: unknown;
    };

    if (!response.ok) {
      const message = formatApiMessage(
        data?.message,
        'Unable to import questions right now.',
      );
      throw new Error(message);
    }

    return data;
  };

  const startUpload = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Only .xlsx files are supported');
      return;
    }

    setFileName(file.name);
    setIsUploading(true);

    const importPromise = uploadFile(file)
      .then((data) => {
        navigate(RP.DATA_UPLOAD_HISTORY_UPLOAD_ID.replace(':uploadId', String(data.id)));
        return data;
      })
      .finally(() => {
        setIsUploading(false);
        setIsDragActive(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      });

    toast.promise(importPromise, {
      pending: 'Staging upload...',
      success: {
        render: ({ data }) => {
          const workbookErrorMessage = getWorkbookErrorMessage(
            data?.summary?.errors,
          );

          if (workbookErrorMessage) {
            return workbookErrorMessage;
          }

          return `Staged ${data?.insertedCount || 0} new questions, ${
            data?.updatedCount || 0
          } updates, ${data?.skippedCount || 0} skips, and ${
            data?.errorCount || 0
          } errors.`;
        },
      },
      error: {
        render: ({ data }) =>
          data instanceof Error ? data.message : 'Question import failed',
      },
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    await startUpload(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isUploading) {
      return;
    }

    event.dataTransfer.dropEffect = 'copy';
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;

    if (nextTarget && event.currentTarget.contains(nextTarget)) {
      return;
    }

    setIsDragActive(false);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isUploading) {
      return;
    }

    setIsDragActive(false);
    const file = event.dataTransfer.files?.[0];

    if (!file) {
      return;
    }

    await startUpload(file);
  };

  return (
    <div className={'mx-auto flex w-full max-w-5xl flex-col gap-6'}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1>Data Upload</h1>
          <p className={'description'}>
            Upload an Excel file for question import. The backend maps these
            human-readable headers to the question fields it needs.
          </p>
          <p className="mt-3 max-w-3xl text-sm text-dark-gray">
            `UsageType` is required. Accepted values are `Real`, `Mock`,
            `Copy`, and `Quiz`.
          </p>
          <p className="mt-3 max-w-4xl text-sm text-dark-gray">
            `Section` is inferred from `Domain`, so you do not need a section
            column in the Excel file. Reading and Writing domains: `Craft and
            Structure`, `Information and Ideas`, `Standard English
            Conventions`, `Expression of Ideas`. Math domains:
            `Problem-Solving and Data Analysis`, `Geometry and Trigonometry`,
            `Advanced Math`, `Algebra`.
          </p>
          <p className="mt-3 max-w-3xl text-sm text-dark-gray">
            `Question_Content` is required for Section 1 (Reading and Writing)
            questions, but it is not used on Math questions, which use
            `Question_Prompt` only.
          </p>
          <p className="mt-3 max-w-3xl text-sm text-dark-gray">
            `Choice_A` through `Choice_D` are required for multiple-choice
            questions, but they are optional for Math student-response
            questions. If a Math row has no choices, it is imported as text
            entry and uses the `Answer` value as the correct response.
          </p>
          <p className="mt-3 max-w-3xl text-sm text-dark-gray">
            Rows with an existing `QID` do not create a new question. The
            importer only adds tags to that existing question when
            `QSourceRelation` is `Repeat_Identical`. Otherwise the row is
            skipped.
          </p>
          <p className="mt-3 max-w-3xl text-sm text-dark-gray">
            Uploading stages a preview first and creates draft questions for
            valid new rows, so you can preview and edit them before final
            apply. Applying the upload publishes those staged drafts.
          </p>
          <p className="mt-3 max-w-3xl text-sm text-dark-gray">
            The header row does not have to be the first row in the workbook.
            The importer scans the first 10 rows, finds the row with the most
            recognized headers, and uses that row as the header row.
          </p>
          <hr className="my-2 opacity-10" />
        </div>
        <Link
          className="mt-1 text-sm font-medium text-blue underline-offset-2 hover:underline"
          to={RP.DATA_UPLOAD_HISTORY}
        >
          View Upload History
        </Link>
      </div>

      <section className="rounded-2xl border border-table-border bg-white p-6">
        <h2 className="text-xl font-semibold text-dark-blue">Upload File</h2>
        <p className="mt-2 text-sm text-dark-gray">
          Accepted format: `.xlsx`. Unknown columns will be ignored during
          import. You can click to choose a file or drag it into this area.
        </p>
        <div
          className={clsx(
            'mt-5 rounded-xl border-2 border-dashed px-5 py-6 transition-colors',
            isDragActive
              ? 'border-blue bg-[rgba(37,78,175,0.06)]'
              : 'border-light-gray bg-light-gray/20',
            isUploading && 'cursor-not-allowed opacity-70',
          )}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            accept=".xlsx"
            className="hidden"
            onChange={handleFileChange}
            ref={fileInputRef}
            type="file"
          />
          <div className="flex flex-wrap items-center gap-3">
            <button
              className="rounded-lg bg-blue px-5 py-3 font-medium text-white hover:text-light-gray disabled:cursor-not-allowed disabled:bg-disabled disabled:hover:text-white"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              {isUploading ? 'Uploading...' : 'Upload Excel File'}
            </button>
            <span className="text-sm text-dark-gray">
              {fileName || 'No file selected'}
            </span>
          </div>
          <p className="mt-3 text-sm text-dark-gray">
            {isDragActive
              ? 'Drop the workbook here'
              : 'Drag and drop an Excel workbook here'}
          </p>
        </div>
      </section>

      <section className="rounded-2xl border border-table-border bg-white p-6">
        <h2 className="text-xl font-semibold text-dark-blue">
          Excel Columns
        </h2>
        <p className="mt-2 text-sm text-dark-gray">
          Use these exact column names in the sheet header row.
        </p>

        <h3 className="mt-5 text-sm font-semibold uppercase tracking-wide text-dark-gray">
          Required
        </h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {requiredColumns.map((column) => (
            <div
              className="rounded-lg border border-light-gray px-4 py-3 text-sm font-medium text-dark-blue"
              key={column}
            >
              {column}
            </div>
          ))}
        </div>

        <h3 className="mt-6 text-sm font-semibold uppercase tracking-wide text-dark-gray">
          Optional
        </h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {optionalColumns.map((column) => (
            <div
              className="rounded-lg border border-light-gray px-4 py-3 text-sm font-medium text-[rgba(37,78,175,0.9)]"
              key={column.label}
            >
              <div>{column.label}</div>
              {column.note && (
                <div className="mt-1 text-xs font-normal text-[rgba(37,78,175,0.75)]">
                  ({column.note})
                </div>
              )}
            </div>
          ))}
        </div>

      </section>
    </div>
  );
};

export default DataUpload;
